import React, {useState} from 'react'

import {noop, range} from 'es-toolkit'

import './_star-rating.scss'

import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

import starOutlineIcon from '../../assets/icons/star-outline.svg'
import starIcon from '../../assets/icons/star.svg'

import Icon from '../icon'

type Props = Overwrite<
  CommonProps<'span'>,
  {
    stars: number
    of?: number
    interactive?: boolean
    size?: 's' | 'default' | 'l'
    onChange?: (rating: number) => void
    onClick?: React.MouseEventHandler
    suffix?: string
  }
>

const StarRating = ({
  className,
  stars,
  of = 5,
  interactive = false,
  size = 'default',
  onChange = noop,
  onClick,
  suffix,
  ...props
}: Props) => {
  const [hover, setHover] = useState(0)

  return (
    <span
      {...props}
      className={cn(
        'StarRating',
        {[`size-${size}`]: true, interactive, clickable: !!onClick},
        className
      )}
      aria-label={`${stars} of ${of}`}
      onClick={onClick}
    >
      <span className="StarRating__stars">
        {range(of).map(index => {
          const star = index + 1

          let width = 0
          let isFilled = false
          let isFilledPartially = false

          if (interactive) {
            isFilled = star <= (hover || stars)
          } else {
            isFilled = star <= (hover || stars)
            isFilledPartially = stars > star - 1 && stars < star

            if (isFilledPartially) {
              width = Math.round((stars % 1) * 100)
            }
          }

          return (
            <span
              key={`star_${star}`}
              className={`StarRating__star StarRating__star${isFilled ? '-filled' : '-empty'}`}
            >
              <span
                className="StarRating__star-fill"
                style={{
                  width: `${width}%`,
                }}
              >
                <Icon source={isFilled ? starOutlineIcon : starIcon} />
              </span>
              <span
                className="StarRating__star-icon"
                onClick={interactive ? () => onChange(star) : undefined}
                onMouseEnter={interactive ? () => setHover(star) : undefined}
                onMouseLeave={interactive ? () => setHover(stars) : undefined}
              >
                <Icon source={isFilled ? starIcon : starOutlineIcon} />
              </span>
            </span>
          )
        })}
      </span>
      <span className="StarRating__suffix">{suffix}</span>
    </span>
  )
}

export default StarRating
